<template>
  <div class="kpt-detail-content-page" v-if="remote">
    <vue-headful :title="`${remote.name} - Overview`" />
    <div class="kpt-detail-content-actions">
      <div v-if="editing">
          <div class="buttons">
            <b-button type="is-primary is-outlined" size="is-small" icon-left="save" :loading="saving" @click="saveRemote" :disabled="!canSave">Save</b-button>
            <b-button type="is-primary is-outlined" size="is-small" icon-left="times" @click="cancelEditingRemote">Cancel</b-button>
          </div>
        </div>
        <div v-if="isTransferVisible">
          <div class="buttons">
            <b-button type="is-primary is-outlined" size="is-small" icon-left="exchange" :loading="isTransferring" :disabled="!isTransferPermitted" @click="transferRemote">Transfer</b-button>
            <b-button type="is-primary is-outlined" size="is-small" icon-left="times" @click="cancelTransferEdit">Cancel</b-button>
          </div>
        </div>
        <div v-if="!editing && !isTransferVisible">
          <div class="level">
            <div class="level-left">
              <div class="level-item">
                <div class="buttons">
                  <b-button type="is-primary is-outlined" icon-left="sync-alt" size="is-small" :disabled="isRefreshing" @click="refreshRemote">Refresh</b-button>
                  <b-button type="is-primary is-outlined" icon-left="edit" size="is-small" :disabled="isRefreshing" @click="editRemote">Edit</b-button>

                  <b-tooltip :active="!!transferRemoteValidationError" 
                    type="is-warning" 
                    :label="transferRemoteValidationError"
                    position="is-right">
                    <b-button type="is-primary is-outlined" icon-left="exchange" size="is-small" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')" :disabled="isRefreshing || !!transferRemoteValidationError || isCancellingPendingTransfer" @click="transferRemoteEdit">Transfer</b-button>
                  </b-tooltip>

                  <b-tooltip :label="optionsFilesDisabledMessage" type="is-warning" :active="!!optionsFilesDisabledMessage" position="is-bottom">
                    <b-button type="is-primary is-outlined" icon-left="download" size="is-small"
                      :disabled="isOptionsFileDownloadDisabled" 
                      :loading="downloadingOptionFile" 
                      @click="downloadOptionsFile" 
                      v-if="!editing">Get Options Files</b-button>
                  </b-tooltip>
                </div>
                
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">
                <b-tooltip :label="`${!isFavorited ? 'Favorite' : 'Unfavorite'} this remote.`" position="is-left">
                  <b-button :type="`is-primary ${!isFavorited ? 'is-outlined' : ''}`" size="is-small" icon-right="star" @click="toggleFavorite"></b-button>
                </b-tooltip>
              </div>
            </div>
          </div>
      </div>
      <div class="action-messages" v-if="errorDownloadingOptionFiles">
        <b-notification
          type="is-danger"
          has-icon
          aria-close-label="Close notification"
          role="alert"
          :active="errorDownloadingOptionFiles"
          @close="errorDownloadingOptionFiles = false">
          There was an error downloading options files.
        </b-notification>
      </div>
      <div class="kpt-transfer-remote-action" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin') && isTransferVisible">
        <b-message type="is-warning" title="Please Note" class="mb-2">
          Transferring a Remote to another Account relinquishes ownership of the Remote. This operation cannot be undone unless the target Account chooses to transfer the Remote back to your Account. 
          Active and/or future subscriptions will also be transferred along with the Remote.
        </b-message>
        <b-field label="Account">
          <b-select placeholder="Select an account" v-model="clonedRemote.account.id" :disabled="isAccountSelectDisabled" @input="validateInputs">
            <option
              v-for="option in allAccounts"
              :value="option.id"
              :key="option.id">
              {{ option.name }}
            </option>
          </b-select>
        </b-field>
        <b-message type="is-info" class="my-4">
          <strong>OR</strong> enter an email address and we will attempt to match the address with an existing Account. If no match is found, the recipient will be invited to establish an account with the Kymeta Access Portal via email.
        </b-message>
        <b-field label="Email Address" 
          :type="{'is-danger' : validationErrors.includes('transferToEmailAddress')}" 
          :message="{'This email is invalid.' : validationErrors.includes('transferToEmailAddress')}">
          <b-input 
            type="email" 
            placeholder="someone@kymetacorp.com" 
            v-model="transferToEmailAddress" 
            @input="emailAddressInputReceived"></b-input>
        </b-field>
      </div>
    </div>
    <div class="columns">
      <div class="column is-half-fullhd">
        <header>
          <h6 class="is-uppercase"><strong>{{ editing ? 'Editing ' : ''}}Details</strong></h6>
        </header>
        <div v-if="!editing">
          <b-message type="is-info" class="my-4" :active="isPendingTransfer" style="position: relative;">
            <p>Remote is pending a transfer to '{{ remote.pendingTransferEmail }}'</p>
            <a class="has-text-danger" @click="cancelPendingTransfer">Cancel Pending Transfer</a>
            <b-loading :is-full-page="false" v-model="isCancellingPendingTransfer" :can-cancel="false"></b-loading>
          </b-message>
          <detail-field v-if="remote.account">
            <template v-slot:label>Account</template>
            <template v-slot:value>{{remote.account.name}}</template>
          </detail-field>
          <detail-field v-if="$hasRole('Super User,Kymeta Admin,Sales Support')">
            <template v-slot:label>Billed Account</template>
            <template v-slot:value>{{ remote.billedAccount ? remote.billedAccount.name : '-' }}</template>
          </detail-field>
          <detail-field v-if="remote.account">
            <template v-slot:label>P. Contact</template>
            <template v-slot:value><a :href="`mailto:${remote.account.primaryContactEmail}`">{{remote.account.primaryContactName}}</a></template>
          </detail-field>
          <detail-field>
            <template v-slot:label>Description</template>
            <template v-slot:value>{{remote.description || 'None set'}}</template>
          </detail-field>
        </div>
        <div v-else>
          <b-field label="Name">
            <b-input type="text" v-model="clonedRemote.name" />
          </b-field>
          <b-field label="Description">
            <b-input type="textarea" maxlength="999" v-model="clonedRemote.description" />
          </b-field>
          <b-field label="Billed Account" v-if="$hasRole('Super User,Kymeta Admin,Sales Support')">
            <b-select placeholder="Select an account" v-model="clonedRemote.billedAccount.id" expanded>
              <option
                v-for="option in accounts"
                :value="option.id"
                :key="option.id">
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
        </div>
        <hr>
        <header>
          <h6 class="is-uppercase"><strong>Device Summary</strong></h6>
        </header>
        <detail-field v-if="remote.devices">
          <template v-slot:label>Terminal</template>
          <template v-slot:value>
            <span title="Terminal Model">{{idToName(remote.devices.terminalModel, $store.state.devices.models)}}<br></span>
            <span title="Terminal Serial">
              <router-link v-if="remote.devices.terminalId" :to="{ name: 'deviceOverview', params: { id: remote.devices.terminalId }}">{{remote.devices.terminalSerial}}</router-link>
              <span v-else>{{remote.devices.terminalSerial}}</span>
            </span>
            <br v-if="remote.devices.terminalSerial">
          </template>
        </detail-field>
        <detail-field v-if="remote.devices">
          <template v-slot:label>Antenna</template>
          <template v-slot:value>
            <span title="Antenna Model">{{idToName(remote.devices.antenna1Model, $store.state.devices.models)}}<br></span>
            <span title="Antenna Serial">
              <router-link v-if="remote.devices.antenna1Id" :to="{ name: 'deviceOverview', params: { id: remote.devices.antenna1Id }}">{{remote.devices.antenna1Serial}}</router-link>
              <span v-else>{{remote.devices.antenna1Serial}}</span>
            </span>
            <br v-if="remote.devices.antenna1Serial">
            <span title="Antenna Serial">{{remote.devices.antenna1CurrentSoftwareVersion}}</span>
          </template>
        </detail-field>
        <detail-field v-if="remote.devices">
          <template v-slot:label>Modem</template>
          <template v-slot:value>
            <span title="Modem Model">{{idToName(remote.devices.modemModel, $store.state.devices.models)}}</span>
            <br v-if="remote.devices.modemSerial">
            <span title="Modem Serial">{{remote.devices.modemSerial}}</span>
          </template>
        </detail-field>
        <detail-field v-if="remote.devices">
          <template v-slot:label>BUC</template>
          <template v-slot:value>
            <span title="BUC Model">{{idToName(remote.devices.bucModel, $store.state.devices.models)}}</span>
          </template>
        </detail-field>
        <detail-field v-if="remote.devices">
          <template v-slot:label>LNB</template>
          <template v-slot:value>
            <span title="LNB Model">{{idToName(remote.devices.lnbModel, $store.state.devices.models)}}</span>
          </template>
        </detail-field>
        <detail-field v-if="remote.devices">
          <template v-slot:label>Cell Router</template>
          <template v-slot:value>
            <span title="Router Model">{{idToName(remote.devices.cellRouterModel, $store.state.devices.models)}}</span>
            <br v-if="remote.devices.cellRouterSerial">
            <span title="Router Serial">{{remote.devices.cellRouterSerial}}</span>
            <span title="Router ICCID">{{remote.devices.cellRouterICCID}}</span>
          </template>
        </detail-field>
        <hr>
        <header>
          <h6 class="is-uppercase"><strong>Subscription Summary</strong></h6>
        </header>
        <div v-if="activeSubscription">
          <detail-field>
            <template v-slot:label>Product</template>
            <template v-slot:value>{{activeSubscription.product}}</template>
          </detail-field>
          <detail-field>
            <template v-slot:label>Data Package</template>
            <template v-slot:value>{{activeSubscription.dataPackage}}</template>
          </detail-field>
          <detail-field>
            <template v-slot:label>Region</template>
            <template v-slot:value>{{activeSubscription.region}}</template>
          </detail-field>
          <detail-field>
            <template v-slot:label>Start Date</template>
            <template v-slot:value>{{ format(addMinutes(new Date(activeSubscription.startDate), new Date(activeSubscription.startDate).getTimezoneOffset()), 'yyyy-MM-dd HH:mm:ss') }} (UTC)</template>
          </detail-field>
          <detail-field>
            <template v-slot:label>End Date</template>
            <template v-slot:value>{{ format(addMinutes(new Date(activeSubscription.endDate), new Date(activeSubscription.endDate).getTimezoneOffset()), 'yyyy-MM-dd HH:mm:ss') }} (UTC)</template>
          </detail-field>
          <div v-if="$hasRole('Super User,Kymeta Admin,Sales Support')" class="textwrap">
            <detail-field>
              <template v-slot:label>Purchase Order</template>
              <template v-slot:value>{{ activeSubscription.purchaseOrder }}</template>
            </detail-field>
            <detail-field>
              <template v-slot:label>Billed Account</template>
              <template v-slot:value>{{ getAccountName(activeSubscription.billedAccountId) }}</template>
            </detail-field>
            <detail-field>
              <template v-slot:label>Is Billable</template>
              <template v-slot:value>{{ activeSubscription.isBillable ? `Yes` : `No` }}</template>
            </detail-field>
          </div>
        </div>
        <div v-else>
          <span>No subscriptions available.</span>
        </div>
      </div>
      <div class="column">
        <remote-connectivity-status :remote="remote" style="margin-bottom: 12px" />
        <remote-detail-map :satellite="satelliteNetwork" :cellular="cellularNetwork" :is-private="remote.private" v-if="satelliteNetwork || cellularNetwork" style="margin-bottom: 12px" />
        <remote-provider-network-rich-state :show-config-link="true" style="margin-bottom: 12px" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')"/>
        <div class="is-flex is-space-between kpt-remote-usage-header">
          <h6 class="is-uppercase"><strong>Usage</strong></h6>
          <h6><strong>{{daysLeft}}</strong></h6>
        </div>
        <remote-data-usage :remote="remote" :subscription="activeSubscription" v-if="activeSubscription" />
      </div>
    </div>
  </div>
</template>
<script>
import { uniqBy, orderBy, isEqual } from 'lodash';
// eslint-disable-next-line object-curly-newline
import { format, endOfMonth, formatDistance, addMinutes, isAfter } from 'date-fns';
import { idToNameParserMixin } from '../../shared/mixins/idToNameParser';
import { dateUtilityMixin } from '../../shared/mixins/dateUtilityMixin';
import RemoteDetailMap from '../components/RemoteDetailMapComponent.vue';
import RemoteConnectivityStatus from '../components/RemoteDetailConnectivityStatusComponent.vue';
import RemoteDataUsage from '../components/RemoteDetailDataUsageComponent.vue';
import RemoteProviderNetworkRichState from '../components/RemoteProviderNetworkRichStateComponent.vue';

export default {
  data() {
    return {
      format,
      addMinutes,
      editing: false,
      saving: false,
      isTransferVisible: false,
      isTransferring: false,
      transferToEmailAddress: null,
      validationErrors: [],
      isTransferPermitted: false,
      isCancellingPendingTransfer: false,
      isAccountSelectDisabled: false,
      clonedRemote: null,
      downloadingOptionFile: false,
      errorDownloadingOptionFiles: false
    };
  },
  mixins: [idToNameParserMixin, dateUtilityMixin],
  computed: {
    isFavorited() {
      return this.$store.getters.remoteFavorites.find(r => r.id == this.remote.id) != null;
    },
    remote() {
      return this.$store.state.remotes.remote;
    },
    activeSubscription() {
      return this.$store.getters.activeSubscription;
    },
    nonActiveSubscriptions() {
      return orderBy(this.$store.state.remotes.remote.subscriptions?.filter(s => !s.active), ['endDate'], ['desc']);
    },
    futureSubscriptions() {
      return this.nonActiveSubscriptions.filter(s => s.endReason != 'UserTerminated' && isAfter(new Date(s.startDate), this.getDateInUTCFormat(new Date())));
    },
    satelliteNetwork() {
      return this.remote.satelliteNetwork;
    },
    cellularNetwork() {
      return this.remote.cellularNetwork;
    },
    isRefreshing() {
      return this.$store.state.remotes.remoteRefreshing;
    },
    isPendingTransfer() {
      return !!this.remote.pendingTransferEmail;
    },
    accounts() {
      return orderBy(this.$store.state.accounts.accounts, ['name'], ['asc']);
    },
    sharedAccounts() {
      return orderBy(this.$store.state.accounts.sharedAccounts, ['name'], ['asc']);
    },
    allAccounts() {
      // join accounts and sharedAccounts arrays
      const concatAccounts = this.accounts.concat(this.sharedAccounts);
      // return unique to prevent duplicates
      return uniqBy(concatAccounts, 'id');
    },
    isSharedRemote() {
      // flatten array to just account id's
      const hierarchyAccountIds = this.accounts.map(a => a.id);
      // check to see if the remote account matches the users account hierarchy
      const hierarchyAccountFound = hierarchyAccountIds.includes(this.remote.account.id);
      return !hierarchyAccountFound;
    },
    isDemoSubscription() {
      if (this.activeSubscription && this.activeSubscription.productShortcode == 'PR_KB_DEMO') return true;
      const sub = this.futureSubscriptions.map(s => s.productShortcode);
      return this.futureSubscriptions && sub.find(s => s == 'PR_KB_DEMO') != null;
    },
    transferRemoteValidationError() {
      if (this.isSharedRemote) return 'Unable to transfer shared Remotes.';
      if (this.isPendingTransfer) return 'Pending transfer has already been initiated.';
      if (this.isDemoSubscription) return 'Unable to transfer remotes with an active or future DEMO subscription.';
      return null;
    },
    canSave() {
      if (!this.editing) return false;
      if (isEqual(this.clonedRemote, this.remote)) return false;
      if (this.saving) return false;
      return true;
    },
    isOptionsFileDownloadDisabled() {
      return !this.activeSubscription
       || this.isRefreshing
       || this.downloadingOptionFile
       || !this.remote?.satellite?.provisioned; // NW provisioned
    },
    optionsFilesDisabledMessage() {
      if (!this.activeSubscription) return 'Active subscription not found.';
      if (!this.remote?.satellite?.provisioned) return 'Remote is not Provisioned on the network.';
      return null;
    },
    daysLeft() {
      const date = new Date();
      const end = endOfMonth(date);
      return `${formatDistance(new Date(), end)} left in ${format(date, 'MMMM')}`;
    }
  },
  components: {
    RemoteDetailMap,
    RemoteConnectivityStatus,
    RemoteDataUsage,
    RemoteProviderNetworkRichState
  },
  methods: {
    async toggleFavorite() {
      await this.$store.dispatch('toggleRemoteFavorite', { id: this.remote.id, name: this.remote.name });
    },
    async refreshRemote() {
      // KyAnalytics client plugin, event tracker
      this.$kya.event('remotes', 'refreshRemote', 'Refresh remote clicked', { remoteId: this.remote.id });
      await this.$store.dispatch('fetchRemote', { id: this.remote.id });
      await this.$store.dispatch('fetchProviderNetworkRichState', { id: this.remote.id });
      return true;
    },
    async downloadOptionsFile() {
      this.errorDownloadingOptionFiles = false;
      this.downloadingOptionFile = true;
      const modemSerial = this.remote.devices && this.remote.devices.modemSerial;
      this.errorDownloadingOptionFiles = !await this.$store.dispatch('downloadOptionsFile', { 
        id: this.remote.id, 
        remoteName: this.remote.name,
        serial: modemSerial 
      });
      this.downloadingOptionFile = false;
      // KyAnalytics client plugin, event tracker
      this.$kya.event('remotes', 'downloadRemoteOptionsFile', 'Download remotes optionFile clicked', { remoteId: this.remote.id });
    },
    transferRemoteEdit() {
      this.clonedRemote = JSON.parse(JSON.stringify(this.remote));
      this.isTransferVisible = true;
    },
    async transferRemote() {
      const payload = {
        id: this.remote.id,
        accountId: this.clonedRemote.account.id != this.remote.account.id ? this.clonedRemote.account.id : null,
        emailAddress: this.transferToEmailAddress
      };

      const originalAccountId = this.remote.account.id;
      this.isTransferring = true;
      const transferResult = await this.$store.dispatch('transferRemote', payload);
      this.isTransferring = false;
      if (!transferResult.isSuccess) {
        this.$buefy.toast.open({
          duration: 3000,
          message: transferResult.error || 'Error encountered while attempting to transfer.',
          type: 'is-danger'
        });
        return;
      }

      // reset the vars for the next transfer or edit
      this.clonedRemote = null;
      this.transferToEmailAddress = null;
      this.isAccountSelectDisabled = false;

      this.isTransferVisible = false;
      const { updatedRemote } = transferResult;
      // check to see if accountId changed
      if (updatedRemote.account.id != originalAccountId) {
        // check account hierarchy to see if user still has permission to remote (if they transferred to a child Account they retain access)
        const userAccounts = this.accounts.map(a => a.id);
        if (!userAccounts.includes(updatedRemote.account.id)) {
          // user no longer has access to the Remote, redirect user to list page & refresh list (ensure remote is not present)
          this.$router.push({ path: '/remotes' });
        }
      }
      // KyAnalytics client plugin, event tracker
      this.$kya.event('remotes', 'transferRemote', 'Transfer remote clicked.', { remoteId: this.remote.id });
    },
    async cancelPendingTransfer() {
      // only send stuff that changed
      this.isCancellingPendingTransfer = true;
      const cancelTransferResult = await this.$store.dispatch('cancelPendingRemoteTransfer', { id: this.remote.id });
      this.isCancellingPendingTransfer = false;
      if (!cancelTransferResult.isSuccess) {
        this.$buefy.toast.open({
          duration: 3000,
          message: cancelTransferResult.error || 'Error encountered while attempting to cancel the pending transfer.',
          type: 'is-danger'
        });
        return;
      }

      // indicate success
      this.$buefy.toast.open({
        duration: 3000,
        message: 'Pending transfer cancelled.',
        type: 'is-success'
      });
    },
    cancelTransferEdit() {
      this.clonedRemote = null;
      this.isTransferVisible = false;
      this.isTransferPermitted = false;
    },
    testEmailAddressValidity(emailAddress) {
      const re = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
      return re.test(emailAddress);
    },
    emailAddressInputReceived() {
      this.validateInputs();

      // reset account and disable dropdown
      if (this.transferToEmailAddress) {
        this.clonedRemote = JSON.parse(JSON.stringify(this.remote));
        this.isAccountSelectDisabled = true;
      } else {
        this.isAccountSelectDisabled = false;
      }
    },
    validateInputs() {
      // reset default
      this.isTransferPermitted = false;
      this.validationErrors = [];

      // validate the email address when it is present
      if (this.transferToEmailAddress && !this.testEmailAddressValidity(this.transferToEmailAddress)) { 
        this.validationErrors.push('transferToEmailAddress');
        this.isTransferPermitted = false;
        return;
      }

      if (!this.transferToEmailAddress && this.clonedRemote.account.id == this.remote.account.id) return;

      this.isTransferPermitted = true;
    },
    editRemote() {
      this.clonedRemote = JSON.parse(JSON.stringify(this.remote));
      
      // billed account is not present, create a shell object for the user to fill
      if (!this.clonedRemote.billedAccount) this.clonedRemote.billedAccount = { id: null };
      this.editing = true;
      // KyAnalytics client plugin, event tracker
      this.$kya.event('remotes', 'showEditRemote', 'Show edit remote.', { remoteId: this.remote.id });
    },
    async saveRemote() {
      const objectToSend = { 
        id: this.clonedRemote.id,
        name: this.clonedRemote.name != this.remote.name ? this.clonedRemote.name : null, 
        description: this.clonedRemote.description != this.remote.description ? this.clonedRemote.description : null
      };
      
      if (this.$hasRole('Super User,Kymeta Admin,Sales Support')) {
        if (!this.remote.billedAccount) {
          objectToSend.billedAccountId = this.clonedRemote.billedAccount ? this.clonedRemote.billedAccount.id : null;
        } else {
          objectToSend.billedAccountId = this.clonedRemote.billedAccount.id != this.remote.billedAccount.id ? this.clonedRemote.billedAccount.id : null;
        }
      }
      // only send stuff that changed
      this.saving = true;
      const updatedRemote = await this.$store.dispatch('updateRemote', objectToSend);
      this.saving = false;
      if (!updatedRemote) {
        this.$buefy.toast.open({
          duration: 3000,
          message: 'Error encountered while attempting to update the Remote.',
          type: 'is-danger'
        });
        return;
      }
      this.editing = false;
      // KyAnalytics client plugin, event tracker
      this.$kya.event('remotes', 'updateRemote', 'Update remote.', { remoteId: this.remote.id });
    },
    cancelEditingRemote() {
      this.clonedRemote = null;
      this.editing = false;
      // KyAnalytics client plugin, event tracker
      this.$kya.event('remotes', 'hideEditRemote', 'Cancelled remote edit.', { remoteId: this.remote.id });
    },
    getAccountName(accountId) {
      if (!accountId || !this.accounts) return null;
      const account = this.accounts.find(a => a.id == accountId);
      return account ? account.name : null;
    }
  }
}
</script>
<style lang="scss" scoped>
@import '../../styles/_colors';

.kpt-detail-content-page {
  .field .icon {
    position: relative;
    top: 2px;
  }
  .kpt-detail-content-actions {
    .b-tooltip {
      margin-right: 8px;

      ::v-deep &.is-warning .tooltip-content {
        top: 40%;
      }
    }
  }
  .kpt-transfer-remote-action {
    margin-top: -12px;
    background-color: $white-bis;
    padding: 12px;
  }
  .action-messages {
    margin-top: -12px;

    ::v-deep .media {
      align-items: center;
    }
  }
  .rdo-active-subscriptions {
    padding: 10px 0px;
    border-bottom: 1px solid $white-ter;

    &:first-child {
      padding-top: 0px;
    }
    &:last-child {
      border-bottom: none;
    }
  }
  .kpt-remote-usage-header {
    margin-bottom: 8px;
  }
  .textwrap{
      word-break:break-word;
  }
}
</style>
